import { useEffect, useRef, useState } from 'react';
import { Checkbox, Label } from '@staff-app/ui';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import {
  ActionButtons,
  CardShell,
  LabeledInput,
  LabeledSelect,
  ResultLine,
  type Msg,
} from './console-ui';
import {
  useApplyOtherCoupon,
  useOtherCouponCatalog,
  useOtherCouponPreview,
  useRemoveOtherCoupon,
  type OtherCouponOutcome,
  type OtherCouponPreview,
} from '../hooks/useOtherCoupons';
import type { BillingCustomerDiscount } from '../hooks/useBillingConsole';

/**
 * OptiHub → **Early Promoters Coupon** (ROOT only).
 *
 * The second coupon surface, over the root-discount list (`public.billing_other_coupons`,
 * migration 040). It is NOT a sixth card inside `BillingConsole.tsx` — that file is over
 * 5 000 lines, and more to the point this console answers to different rules:
 *
 *   - **no certificate gate.** Nothing here reads or files a document.
 *   - **it may REPLACE** the discount the customer already carries, on an explicit tick.
 *     The Billing Console's card refuses instead, and that is deliberately unchanged.
 *   - **it may stamp two Stripe segment flags**, add-only.
 *   - **the comment is written to Stripe**, as `optisigns_staff_note` on the customer, on top
 *     of the audit row every card files. It is required here as it is there — what is new is
 *     where it ends up.
 *
 * The tab is hidden for a non-root operator by `OptiHubPage`, and this component re-checks —
 * matching `HardwareProductsPage`. Neither is the real gate: every op behind it is
 * `@RequireRoles(AdminRole.ROOT)` server-side, because the mutation is reachable without the
 * tab.
 */

/** Same shape check as the Billing Console's, so one console does not accept what the other refuses. */
const CUS_LOOKUP_RE = /^cus_\w{6,}$/i;

// DISPLAY vs IDENTIFIER. The tab reads "Early Promoters Coupon" and the card title reads
// "Apply Early Promoters Coupon Code"; both are copy and have already changed once. These two
// are keys — they name the testids, and through them the e2e scenarios — so they do NOT follow
// a rename. Same rule as the Apply Stripe Coupon card, whose key is still `apply-coupon`.
const KEY = 'apply-coupon-code';
const TESTID = 'coupon-code';

/**
 * How long the id has to stop changing before the customer is read.
 *
 * A `cus_` id is pasted far more often than typed, and a paste lands in one event — so this is
 * mostly about the typed case, where firing per keystroke would put a dozen reads (each one
 * resolving a company in Mongo) behind a single id. Short enough to feel immediate on a paste,
 * long enough that typing costs one read.
 */
const LOOKUP_DEBOUNCE_MS = 350;

/** `repeating 3 months` / `once` / `forever` — what the operator has to be able to say out loud. */
function durationLabel(d: BillingCustomerDiscount): string {
  if (d.duration === 'repeating') {
    return d.durationInMonths ? `repeating ${d.durationInMonths} months` : 'repeating';
  }
  return d.duration;
}

/**
 * The `YYYY-MM-DD` an operator reads.
 *
 * `startedAt` crosses the wire as an ISO string (it is a `Date` on the server), so this slices
 * rather than doing arithmetic — and it is display only. Nothing here decides anything on a
 * date: the idempotency window and the read-back comparison are both server-side, on Stripe's
 * own epoch seconds.
 */
function onDate(iso: string): string {
  return (iso ?? '').slice(0, 10);
}

/**
 * What Stripe holds on this customer today: the discount, the tax status and the two segment
 * flags.
 *
 * The flags are rendered READ-ONLY here, separately from the tick boxes below, and that
 * separation is the whole design of this card's flag half. The boxes are add-only — ticking
 * writes `"true"`, unticking sends nothing — so a box pre-ticked from this state would invite
 * an operator to untick it and expect the flag to come off. It would not. State is reported;
 * the boxes are an instruction.
 */
function CustomerPanel({ preview }: { preview: OtherCouponPreview }) {
  const d = preview.currentDiscount;
  return (
    <div
      className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3 text-xs"
      data-testid={`${TESTID}-customer-panel`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-semibold text-foreground">
          {preview.companyName ?? preview.accountId}
        </span>
        <span className="text-muted-foreground">{preview.stripeId}</span>
        {preview.stripeName && preview.stripeName !== preview.companyName ? (
          <span className="text-muted-foreground">{`(Stripe: ${preview.stripeName})`}</span>
        ) : null}
      </div>

      {d ? (
        <p className="text-destructive" data-testid={`${TESTID}-current-discount`}>
          {`⚠ Currently ${d.couponId} ("${d.couponName ?? '(unnamed)'}", ${d.summary}, ` +
            `${durationLabel(d)}) since ${onDate(d.startedAt)}. Stripe holds exactly one ` +
            `customer-level discount and keeps NO history of one it displaced — applying ` +
            `another means this one is gone for good.`}
        </p>
      ) : (
        <p className="text-muted-foreground" data-testid={`${TESTID}-no-discount`}>
          {'No customer-level discount today.'}
        </p>
      )}

      <p className="text-muted-foreground">
        {`Tax status: ${preview.taxStatus ?? 'unknown'}`}
      </p>

      <p className="text-muted-foreground" data-testid={`${TESTID}-flags-today`}>
        {'Stripe flags today: '}
        <span className={preview.flagEarlyPromotors ? 'text-foreground' : undefined}>
          {`early_promotors ${preview.flagEarlyPromotors ? '✓' : '—'}`}
        </span>
        {'   '}
        <span className={preview.flagReseller ? 'text-foreground' : undefined}>
          {`reseller ${preview.flagReseller ? '✓' : '—'}`}
        </span>
        {preview.flagGrantedBy ? ` · last set by ${preview.flagGrantedBy}` : ''}
      </p>
    </div>
  );
}

/**
 * The outcome panel, for an apply AND a remove — they answer the same question ("what does
 * this customer have now, and where do I check it"), so one panel renders both. Read it the
 * other way round after a removal: `discount` is null and `previousCouponId` names what went.
 *
 * Reads what LANDED in Stripe, never what the form asked for.
 */
function Outcome({ result }: { result: OtherCouponOutcome & { flagsApplied?: string[] } }) {
  return (
    <div
      className="flex flex-col gap-1 rounded-md border bg-card p-3 text-xs"
      data-testid={`${TESTID}-result`}
    >
      <p className="text-foreground">{result.message}</p>
      {result.previousCouponId ? (
        <p className="text-destructive" data-testid={`${TESTID}-result-displaced`}>
          {`${result.discount ? 'Displaced' : 'Removed'} ${result.previousCouponId} — it is gone ` +
            `from Stripe, and the audit log is now the only record of it.`}
        </p>
      ) : null}
      {result.flagsApplied?.length ? (
        <p className="text-muted-foreground" data-testid={`${TESTID}-result-flags`}>
          {`Metadata written: ${result.flagsApplied.join(', ')}.`}
        </p>
      ) : null}
      {result.taxStatusChanged ? (
        <p className="text-muted-foreground" data-testid={`${TESTID}-result-tax`}>
          {`Tax status is now "${result.taxStatus}" — this request changed it, because the ` +
            `coupon involved was the reason for the exemption.`}
        </p>
      ) : null}
      <a
        className="text-primary underline"
        href={result.customerUrl}
        target="_blank"
        rel="noreferrer"
      >
        {'Open the customer in Stripe'}
      </a>
    </div>
  );
}

export function CouponCodeConsole() {
  const me = useAuthStore((s) => s.adminUser);
  const isRoot = !!me?.roles?.includes(ADMIN_ROLES.ROOT);

  const empty = { stripeId: '', couponKey: '', comment: '' };
  const [f, setF] = useState({ ...empty });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  /**
   * What staff-api last read for this customer. Held locally rather than read off the lazy
   * query so that changing the id can DROP it — a preview belonging to a previous customer is
   * worse than none, because it is what decides whether Replace is demanded.
   */
  const [preview, setPreview] = useState<OtherCouponPreview | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  // ADD-ONLY. These always start false and are never seeded from the preview — see
  // `CustomerPanel`. Unticking one sends nothing; it cannot clear a flag.
  const [flagEarlyPromotors, setFlagEarlyPromotors] = useState(false);
  const [flagReseller, setFlagReseller] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [result, setResult] = useState<(OtherCouponOutcome & { flagsApplied?: string[] }) | null>(
    null,
  );
  /**
   * Two-step confirm on the removal. A removal is a price INCREASE, it is irreversible in the
   * sense that matters — Stripe keeps no history of the discount, so nothing can reconstruct a
   * negotiated rate once it is gone — and it sits next to an Apply button. One misplaced click
   * should not be able to do it.
   */
  const [removeArmed, setRemoveArmed] = useState(false);

  const { data: catalogData, loading: catalogLoading, error: catalogError } =
    useOtherCouponCatalog();
  const [lookupPreview, { loading: previewLoading }] = useOtherCouponPreview();
  const [applyCoupon] = useApplyOtherCoupon();
  const [removeCoupon] = useRemoveOtherCoupon();

  const catalog = catalogData?.staffBillingOtherCouponCatalog;
  const coupons = catalog?.coupons ?? [];
  const selected = coupons.find((c) => c.couponKey === f.couponKey) ?? null;

  /** A DIFFERENT coupon is already on the customer — the only case Replace is about. */
  const displacing =
    !!preview?.currentDiscount &&
    !!selected &&
    preview.currentDiscount.couponId !== selected.stripeCouponId;

  /**
   * Read the customer: the company name that proves the id is the intended tenant, the
   * discount they already have, and the flags they already carry.
   *
   * Fired on blur, and a failure is SHOWN: the apply is gated on this read, so failing
   * quietly would leave the operator with a disabled button and no reason for it.
   */
  const resolvePreview = async () => {
    const id = f.stripeId.trim();
    if (!CUS_LOOKUP_RE.test(id)) return;
    if (preview?.stripeId === id) return;
    setLookupError(null);
    try {
      const r = await lookupPreview({ variables: { stripeId: id } });
      // useLazyQuery resolves with the error rather than rejecting for a GraphQL error, and
      // rejects for some transport failures — so both paths are handled.
      if (r.error || !r.data) {
        setPreview(null);
        setLookupError(r.error?.message ?? 'staff-api returned no customer for that id.');
        return;
      }
      setPreview(r.data.staffBillingOtherCouponPreview);
    } catch (e) {
      setPreview(null);
      setLookupError((e as Error)?.message ?? 'Could not read the customer.');
    }
  };

  /**
   * AUTO-LOOKUP. The read fires on its own as soon as the id looks like a customer, debounced,
   * so an operator who pastes `cus_…` sees the company, the discount and the flags without
   * pressing anything.
   *
   * Held in a ref rather than listed as a dependency: `resolvePreview` is a new function every
   * render and closes over state it also sets, so depending on it directly would re-run the
   * effect on its own result — a read loop against Mongo and Stripe.
   *
   * The deps are the id and the id ALREADY loaded. That is what stops a re-fire once the read
   * lands, and equally what stops a retry storm after a FAILED one: the effect has run for that
   * id and neither value has changed, so nothing re-triggers until the operator edits the field
   * or presses the button. Which is why the button stays — a failed read, or a customer changed
   * in Stripe while this tab sat open, needs a way to ask again.
   */
  const resolveRef = useRef(resolvePreview);
  resolveRef.current = resolvePreview;
  useEffect(() => {
    const id = f.stripeId.trim();
    if (!CUS_LOOKUP_RE.test(id)) return;
    if (preview?.stripeId === id) return;
    const timer = setTimeout(() => void resolveRef.current(), LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [f.stripeId, preview?.stripeId]);

  /**
   * Changing the customer drops everything read for the previous one, and un-ticks Replace.
   * A consent to displace THAT customer's discount must not survive into a request aimed at
   * a different one.
   */
  const changeStripeId = (stripeId: string) => {
    setF((s) => ({ ...s, stripeId }));
    setPreview(null);
    setLookupError(null);
    setReplaceExisting(false);
    setRemoveArmed(false);
  };

  const clear = () => {
    setF({ ...empty });
    setPreview(null);
    setLookupError(null);
    setReplaceExisting(false);
    setRemoveArmed(false);
    setFlagEarlyPromotors(false);
    setFlagReseller(false);
    setMsg(null);
    setResult(null);
  };

  /**
   * At least one segment flag is REQUIRED (requester, 2026-09-15) — every apply from this tab
   * classifies the customer. The server refuses without one by name; this only saves the
   * round-trip.
   */
  const anyFlag = flagEarlyPromotors || flagReseller;

  /**
   * Submit is gated on the preview, not only on the fields: without it the card cannot know
   * whether a discount is being displaced, and would offer Apply on a request the server
   * refuses for a reason the operator was never shown.
   */
  const valid =
    !!f.couponKey &&
    !!f.comment.trim() &&
    CUS_LOOKUP_RE.test(f.stripeId.trim()) &&
    !!preview &&
    anyFlag &&
    (!displacing || replaceExisting);

  /**
   * Take the customer's current discount off.
   *
   * Requires NOTHING but the discount itself (requester, 2026-09-15): no coupon, no segment
   * flag, and no comment. A removal names nothing and classifies nobody, and root working
   * through their own cohort should not have to justify each one in prose. A comment typed in
   * the shared field above is still sent and still recorded — optional, not discarded.
   *
   * Re-reads the customer afterwards rather than patching state locally, so the panel shows
   * what Stripe holds rather than what we believe we did.
   */
  const removeDiscount = async () => {
    if (!preview?.currentDiscount) return;
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const r = (
        await removeCoupon({
          variables: { stripeId: f.stripeId.trim(), comment: f.comment.trim() },
        })
      ).data!.staffBillingRemoveOtherCoupon;

      setResult(r);
      setMsg({ kind: 'ok', text: r.message });
      setRemoveArmed(false);
      setReplaceExisting(false);
      try {
        const again = await lookupPreview({ variables: { stripeId: f.stripeId.trim() } });
        if (again.data) setPreview(again.data.staffBillingOtherCouponPreview);
      } catch {
        // The removal stands either way; the outcome panel is its record.
      }
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Could not remove the discount.' });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const r = (
        await applyCoupon({
          variables: {
            input: {
              stripeId: f.stripeId.trim(),
              // The list KEY, never `selected.stripeCouponId`: staff-api re-resolves the
              // coupon from its own row, so nothing sent from here can attach a coupon
              // nobody put on the list.
              couponKey: f.couponKey,
              comment: f.comment.trim(),
              // Sent only when it means something. `replaceExisting` on a customer with no
              // discount is a consent to nothing, and would read in the audit row as though
              // the operator expected to displace something.
              ...(displacing ? { replaceExisting: true } : {}),
              ...(flagEarlyPromotors ? { flagEarlyPromotors: true } : {}),
              ...(flagReseller ? { flagReseller: true } : {}),
            },
          },
        })
      ).data!.staffBillingApplyOtherCoupon;

      setResult(r);
      setMsg({ kind: 'ok', text: r.message });
      // Re-read so the panel shows what Stripe holds NOW — including the flags this write
      // set. Silent: the apply has already happened and the outcome panel is its record.
      try {
        const again = await lookupPreview({ variables: { stripeId: f.stripeId.trim() } });
        if (again.data) setPreview(again.data.staffBillingOtherCouponPreview);
      } catch {
        // The write stands either way.
      }
      setReplaceExisting(false);
      setFlagEarlyPromotors(false);
      setFlagReseller(false);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Could not apply the coupon.' });
    } finally {
      setBusy(false);
    }
  };

  if (!isRoot) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${TESTID}-forbidden`}>
        {'Early Promoters Coupon is available to root operators only.'}
      </p>
    );
  }

  return (
    <CardShell
      testId={`${TESTID}-card`}
      title="Apply Early Promoters Coupon Code"
      description="ROOT ONLY — support operators do not see this tab, and every operation behind it is refused for them by staff-api, not merely hidden. Attaches a coupon from the root-discount list to the CUSTOMER, so it discounts every future subscription invoice for the coupon's duration — nothing is charged or refunded now. Unlike Apply Stripe Coupon this list has no certificate gate, and root may REPLACE the discount the customer already has: Stripe keeps no history of one it displaced, so the replacement is permanent and the audit log becomes the only record of what was there. Hardware invoices are never discounted. If a discount does not show up on an invoice, check the subscription in Stripe — a subscription-level coupon overrides a customer one."
    >
      {catalogError || catalog?.couponsUnavailable ? (
        <p className="text-xs text-destructive" data-testid={`${TESTID}-catalog-error`}>
          {catalogError
            ? `Could not load the root-discount list: ${catalogError.message}`
            : 'The root-discount coupon list is not migrated in this environment, so there is nothing to apply. This is a missing table, not an empty list — it needs migration 040 run against this database.'}
        </p>
      ) : null}

      <LabeledInput
        id={`${KEY}-stripeId`}
        label="Stripe customer ID"
        value={f.stripeId}
        onChange={changeStripeId}
        placeholder="cus_..."
        helper="Resolved to an OptiSigns account server-side — the company name below is the proof you have the right customer."
        testId={`${TESTID}-stripeId`}
      />

      {previewLoading ? (
        <p className="text-xs text-muted-foreground">{'Reading the customer…'}</p>
      ) : null}
      {lookupError ? (
        <p className="text-xs text-destructive" data-testid={`${TESTID}-lookup-error`}>
          {lookupError}
        </p>
      ) : null}
      {preview ? <CustomerPanel preview={preview} /> : null}

      {/*
        Remove the discount the customer already has.
        Only rendered when there IS one — there is nothing to offer otherwise, and an
        always-visible destructive control next to Apply is an accident waiting to happen.

        Two steps, and the second one names the coupon. The comment is shared with the apply
        because it answers the same question (why), and the server demands it either way.
      */}
      {preview?.currentDiscount ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-destructive/40 p-3"
          data-testid={`${TESTID}-remove-row`}
        >
          {!removeArmed ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {'Taking this discount off instead? Future invoices bill at list price; already-finalized ones are unchanged.'}
              </p>
              <button
                type="button"
                className="shrink-0 rounded-md border border-destructive px-3 py-1.5 text-xs font-medium text-destructive disabled:opacity-50"
                onClick={() => setRemoveArmed(true)}
                disabled={busy}
                data-testid={`${TESTID}-remove`}
              >
                {'Remove discount'}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2" data-testid={`${TESTID}-remove-confirm-row`}>
              <p className="text-xs text-destructive">
                {`Remove ${preview.currentDiscount.couponId} ("${preview.currentDiscount.couponName ?? '(unnamed)'}", ` +
                  `${preview.currentDiscount.summary}) from ${preview.companyName ?? preview.accountId}? ` +
                  `Stripe keeps NO history of it — once it is gone nothing can reconstruct what ` +
                  `this customer was on. If it was backed by an exemption certificate, they also ` +
                  `go back to Taxable.`}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {f.comment.trim()
                  ? 'The comment above will be recorded with this removal.'
                  : 'No comment needed. Anything typed in the comment field above is recorded with it.'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground disabled:opacity-50"
                  onClick={removeDiscount}
                  disabled={busy}
                  data-testid={`${TESTID}-remove-confirm`}
                >
                  {busy ? 'Working...' : 'Yes, remove it'}
                </button>
                <button
                  type="button"
                  className="rounded-md px-3 py-1.5 text-xs text-muted-foreground"
                  onClick={() => setRemoveArmed(false)}
                  disabled={busy}
                  data-testid={`${TESTID}-remove-cancel`}
                >
                  {'Cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* The read happens on its own (see the auto-lookup effect). This stays for the two
          cases it cannot cover: asking again after a failed read, and re-reading a customer
          somebody changed in Stripe while this tab sat open. */}
      <button
        type="button"
        className="self-start text-xs text-primary underline disabled:opacity-50"
        onClick={resolvePreview}
        disabled={!CUS_LOOKUP_RE.test(f.stripeId.trim()) || previewLoading}
        data-testid={`${TESTID}-lookup`}
      >
        {preview ? 'Re-read the customer' : 'Look up now'}
      </button>

      {/* The picker is gated on the comment, as the Billing Console's card is: naming the
          reason before choosing the discount is the order that keeps the audit row useful. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledSelect
          id={`${KEY}-couponKey`}
          label="Coupon"
          // The list KEY is the value: no Stripe coupon id ever leaves this card.
          value={f.couponKey}
          onChange={set('couponKey')}
          placeholder={
            catalogLoading ? 'Loading…' : coupons.length ? 'Select coupon' : 'No coupons configured'
          }
          options={coupons.map((c) => ({
            label: c.discountSummary ? `${c.label} — ${c.discountSummary}` : c.label,
            value: c.couponKey,
          }))}
          helper="Active rows from the root-discount list — ROOT maintains it on Administration → Products List."
          testId={`${TESTID}-couponKey`}
        />
        <LabeledInput
          id={`${KEY}-comment`}
          label="Comment"
          value={f.comment}
          onChange={set('comment')}
          placeholder="Why this discount was granted"
          helper="Required. Recorded in the audit row AND written onto the Stripe customer as optisigns_staff_note, so the reason sits next to the discount in the Stripe dashboard. Applying another coupon later overwrites it."
          testId={`${TESTID}-comment`}
        />
      </div>

      {selected?.terms ? (
        <p className="text-[11px] text-muted-foreground">{selected.terms}</p>
      ) : null}

      {/* Shown ONLY when a different discount is actually in the way, and never pre-ticked.
          Consent to destroy a record Stripe does not keep has to be a deliberate act. */}
      {displacing ? (
        <div className="flex items-start gap-2" data-testid={`${TESTID}-replace-row`}>
          <Checkbox
            id={`${KEY}-replace`}
            checked={replaceExisting}
            onCheckedChange={(v) => setReplaceExisting(v === true)}
            data-testid={`${TESTID}-replace`}
          />
          <Label htmlFor={`${KEY}-replace`} className="text-xs font-normal">
            {`Replace ${preview?.currentDiscount?.couponId} with ${selected?.label}. The current ` +
              `discount is removed first and Stripe keeps no history of it — if it was backed by ` +
              `an exemption certificate, the customer also goes back to Taxable.`}
          </Label>
        </div>
      ) : null}

      <div className="flex flex-col gap-2" data-testid={`${TESTID}-flags`}>
        <p className="text-xs font-medium text-foreground">
          {'Add flags '}
          <span className="font-normal text-muted-foreground">{'(at least one required)'}</span>
        </p>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${KEY}-flag-early`}
            checked={flagEarlyPromotors}
            onCheckedChange={(v) => setFlagEarlyPromotors(v === true)}
            data-testid={`${TESTID}-flag-early-promotors`}
          />
          <Label htmlFor={`${KEY}-flag-early`} className="text-xs font-normal">
            {'early_promotors'}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${KEY}-flag-reseller`}
            checked={flagReseller}
            onCheckedChange={(v) => setFlagReseller(v === true)}
            data-testid={`${TESTID}-flag-reseller`}
          />
          <Label htmlFor={`${KEY}-flag-reseller`} className="text-xs font-normal">
            {'reseller'}
          </Label>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {'Ticking adds the flag to the Stripe customer’s metadata as "true", alongside early_promoters_granted_by. Unticking never REMOVES one — clear a flag in the Stripe dashboard. (The spelling of early_promotors matches what is already on live customers; it is not a typo here.)'}
        </p>
        {/* Shown rather than left to a greyed-out button: "why can I not submit" is the one
            question a disabled control cannot answer, and this is the only required field
            that is not a text box an operator can see is empty. Held back until the customer
            is read, so it is guidance at the point of decision rather than an error on an
            untouched form. */}
        {preview && !anyFlag ? (
          <p className="text-[11px] text-destructive" data-testid={`${TESTID}-flags-required`}>
            {'Pick at least one — every discount granted here classifies the customer, and the server refuses without it.'}
          </p>
        ) : null}
      </div>

      <ActionButtons
        keyId={KEY}
        submitLabel="Apply coupon"
        valid={valid}
        busy={busy}
        onSubmit={submit}
        onClear={clear}
        testIdPrefix={TESTID}
      />

      <ResultLine msg={msg} testId={`${TESTID}-msg`} />
      {result ? <Outcome result={result} /> : null}
    </CardShell>
  );
}
